import { useQuery } from '@tanstack/react-query'

import { isAdvertiser } from '~/helpers'

import {
  getCampaignPermissionsService,
  getCampaignPermissionsServiceKey
} from '../services/configuration/campaign-permissions'

type UseImpressionShareEnabledParams = {
  publisherId?: string | null
  advertiserId?: string | null
  /**
   * Value while permissions are unresolved (no publisher scope, query still
   * pending, or request error). Tables fail open (`true`) to avoid flicker;
   * news/toasts should pass `false` so the banner never appears for
   * non-entitled accounts — including when the permissions call fails.
   */
  defaultEnabled?: boolean
}

export function useImpressionShareEnabled({
  publisherId,
  advertiserId,
  defaultEnabled = true
}: UseImpressionShareEnabledParams): boolean {
  const publisher_id = publisherId ?? null
  const advertiser_id = advertiserId ?? null
  const advertiserSession = isAdvertiser()
  const requestParams = {
    publisher_id,
    ...(advertiserSession && advertiser_id ? { advertiser_id } : {})
  }

  const { data, isSuccess } = useQuery({
    enabled: !!publisher_id,
    queryKey: [getCampaignPermissionsServiceKey, requestParams],
    queryFn: async () => {
      const response = await getCampaignPermissionsService(requestParams)

      return response.data
    },
    staleTime: 5 * 60 * 1000
  })

  // Gate on isSuccess (not isFetched): errors set isFetched but leave data
  // undefined, which would otherwise fail open via `?? true`.
  if (!publisher_id || !isSuccess) return defaultEnabled

  return data?.allow_impression_share ?? true
}
